import { useEffect, useMemo, useRef, useState } from 'react';
import { getCurrentTradingDayStart, getNextTradingDayReset } from './timeUtils';
import resolveApiBase from './apiBase';

const STRATEGY_ID = 'pelican';
const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;
const isSseSupported = typeof window !== 'undefined' && 'EventSource' in window;
const FRESHNESS_THRESHOLD_MS = 2 * 60 * 1000;

const resolveUrl = (path) => {
    if (!API_BASE) {
        return path;
    }
    const normalisedBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${normalisedBase}${path.startsWith('/') ? path : `/${path}`}`;
};

const createInitialSummary = () => ({
    status: 'initialising',
    lastHeartbeatAt: null,
    lastMessage: 'Awaiting telemetry…',
    netPnl: 0,
    liveLayers: 0,
    simulationLayers: 0,
});

const createInitialState = () => ({
    strategyId: STRATEGY_ID,
    summary: createInitialSummary(),
    layers: [],
    events: [],
    activity: [],
    accounts: [],
});

const normaliseEvent = (event) => ({
    strategyId: event?.strategyId || STRATEGY_ID,
    severity: event?.severity || 'info',
    occurredAt: event?.occurredAt || null,
    message: event?.message || '',
    meta: event?.meta || null,
});

const eventHash = (event) => `${event.occurredAt || ''}::${event.message || ''}`;

const applyLayerSnapshot = (currentState, snapshot) => {
    if (!snapshot?.layerId) {
        return currentState;
    }

    const incoming = {
        ...snapshot,
        layerId: snapshot.layerId,
        capturedAt: snapshot.capturedAt || new Date().toISOString(),
    };

    const layers = Array.isArray(currentState.layers) ? [...currentState.layers] : [];
    const index = layers.findIndex((layer) => layer.layerId === incoming.layerId);

    if (index >= 0) {
        layers[index] = { ...layers[index], ...incoming };
    } else {
        layers.push(incoming);
    }

    return {
        ...currentState,
        layers,
        summary: {
            ...currentState.summary,
            netPnl: incoming.netPnl ?? currentState.summary.netPnl,
            liveLayers: layers.filter((layer) => (layer.status || '').toLowerCase() === 'active').length,
            simulationLayers: layers.filter((layer) => (layer.status || '').toLowerCase() === 'simulation').length,
            lastHeartbeatAt: incoming.capturedAt ?? currentState.summary.lastHeartbeatAt,
        },
    };
};

const appendEvent = (currentState, eventPayload) => {
    if (!eventPayload?.message) {
        return currentState;
    }

    const event = normaliseEvent(eventPayload);
    const existing = new Set((currentState.events || []).map(eventHash));
    const events = existing.has(eventHash(event))
        ? currentState.events
        : [event, ...(currentState.events || [])];
    const trimmed = events.slice(0, 50);

    const summary = { ...currentState.summary };
    summary.lastMessage = event.message || summary.lastMessage;
    summary.lastHeartbeatAt =
        event.severity === 'heartbeat'
            ? event.occurredAt || summary.lastHeartbeatAt
            : summary.lastHeartbeatAt || event.occurredAt || null;

    if (event.severity === 'error') {
        summary.status = 'error';
    } else if (event.severity === 'warning' && summary.status !== 'error') {
        summary.status = 'warning';
    } else if (event.severity === 'heartbeat' && summary.status === 'initialising') {
        summary.status = 'operational';
    }

    return {
        ...currentState,
        summary,
        events: trimmed,
    };
};

const applyActivitySnapshot = (currentState, snapshotInput) => {
    if (!snapshotInput) {
        return currentState;
    }

    const fallbackAccount = currentState.summary?.accountId || 'primary';
    const snapshot = {
        ...snapshotInput,
        strategyId: snapshotInput.strategyId || STRATEGY_ID,
        accountId: snapshotInput.accountId || fallbackAccount,
        capturedAt: snapshotInput.capturedAt || new Date().toISOString(),
        activityKey:
            snapshotInput.activityKey ||
            `${snapshotInput.strategyId || STRATEGY_ID}:${snapshotInput.accountId || fallbackAccount}`,
        orders: Array.isArray(snapshotInput.orders) ? snapshotInput.orders : [],
        positions: Array.isArray(snapshotInput.positions) ? snapshotInput.positions : [],
        stops: Array.isArray(snapshotInput.stops) ? snapshotInput.stops : [],
    };

    const activity = Array.isArray(currentState.activity) ? [...currentState.activity] : [];
    const index = activity.findIndex((entry) => entry.activityKey === snapshot.activityKey);
    if (index >= 0) {
        activity[index] = { ...activity[index], ...snapshot };
    } else {
        activity.unshift(snapshot);
    }

    const trimmed = activity.slice(0, 25);
    const summary = {
        ...currentState.summary,
    };

    if (snapshot.summary?.netPnl != null) {
        summary.netPnl = snapshot.summary.netPnl;
    }
    if (snapshot.summary?.status) {
        summary.status = snapshot.summary.status;
    }
    if (snapshot.capturedAt) {
        summary.lastHeartbeatAt = snapshot.capturedAt;
    }

    return {
        ...currentState,
        summary,
        activity: trimmed,
    };
};

const deriveAccountStatus = (strategies = []) => {
    if (!strategies.length) {
        return 'initialising';
    }
    const statuses = strategies.map((strategy) => (strategy.status || '').toLowerCase());
    if (statuses.some((status) => ['error', 'failed', 'halted'].includes(status))) {
        return 'error';
    }
    if (statuses.some((status) => ['warning', 'degraded'].includes(status))) {
        return 'warning';
    }
    if (statuses.some((status) => ['active', 'operational', 'connected', 'live'].includes(status))) {
        return 'operational';
    }
    if (statuses.every((status) => status === 'closed')) {
        return 'closed';
    }
    return statuses[0] || 'initialising';
};

const deriveAccounts = (state) => {
    const layers = Array.isArray(state.layers) ? state.layers : [];
    const events = Array.isArray(state.events) ? state.events : [];
    const activityEntries = Array.isArray(state.activity) ? state.activity : [];
    const accountMap = new Map();

    const ensureAccount = (source) => {
        const fallbackId = state.summary?.accountId || 'primary';
        const accountId = source?.accountId || fallbackId;
        const accountName = source?.accountName || state.summary?.accountName || 'Primary Account';
        const accountAlias = source?.accountAlias || state.summary?.accountAlias || accountName || accountId;
        const clientName = source?.meta?.client || state.summary?.client || accountAlias || accountName || accountId;

        if (!accountMap.has(accountId)) {
            accountMap.set(accountId, {
                accountId,
                name: accountName,
                alias: accountAlias,
                client: clientName,
                balance: source?.balance ?? null,
                risk: source?.risk ?? null,
                strategies: new Map(),
            });
        }
        return accountMap.get(accountId);
    };

    const ensureStrategy = (account, source) => {
        const fallbackStrategyId = state.summary?.strategyId || STRATEGY_ID;
        const strategyId = source?.strategyId || fallbackStrategyId;
        const strategyLabel = source?.strategyLabel || state.summary?.strategyLabel || strategyId;
        if (!account.strategies.has(strategyId)) {
            account.strategies.set(strategyId, {
                strategyId,
                name: strategyLabel,
                status: state.summary?.status || 'initialising',
                netPnl: state.summary?.netPnl ?? 0,
                layers: [],
                events: [],
                orders: [],
                positions: [],
                summary: {
                    ...(state.summary || {}),
                    strategyId,
                    strategyLabel,
                    accountId: account.accountId,
                    accountName: account.name,
                    accountAlias: account.alias,
                    client: account.client,
                },
            });
        }
        return account.strategies.get(strategyId);
    };

    layers.forEach((layer) => {
        const account = ensureAccount(layer);
        const strategy = ensureStrategy(account, layer);
        strategy.layers = [
            ...strategy.layers.filter((candidate) => candidate.layerId !== layer.layerId),
            layer,
        ];
        if (layer.netPnl != null) {
            strategy.netPnl = layer.netPnl;
            strategy.summary.netPnl = layer.netPnl;
        }
        if (layer.status) {
            strategy.status = layer.status;
            strategy.summary.status = layer.status;
        }
        if (layer.capturedAt) {
            strategy.summary.lastHeartbeatAt = layer.capturedAt;
        }
    });

    events.forEach((event) => {
        const account = ensureAccount(event);
        const strategy = ensureStrategy(account, event);
        const duplicate = strategy.events.some((existing) => eventHash(existing) === eventHash(event));
        if (!duplicate) {
            strategy.events = [event, ...strategy.events].slice(0, 50);
        }
        if (event.message) {
            strategy.summary.lastMessage = event.message;
        }
        if (event.occurredAt) {
            strategy.summary.lastHeartbeatAt = strategy.summary.lastHeartbeatAt || event.occurredAt;
        }
        if (event.severity === 'error') {
            strategy.status = 'error';
            strategy.summary.status = 'error';
        } else if (event.severity === 'warning' && strategy.status !== 'error') {
            strategy.status = 'warning';
            strategy.summary.status = 'warning';
        } else if (event.severity === 'heartbeat') {
            strategy.summary.lastHeartbeatAt = event.occurredAt || strategy.summary.lastHeartbeatAt;
        }
    });

    activityEntries.forEach((activity) => {
        const account = ensureAccount(activity);
        const strategy = ensureStrategy(account, activity);
        if (Array.isArray(activity.orders)) {
            strategy.orders = activity.orders;
        }
        if (Array.isArray(activity.positions)) {
            strategy.positions = activity.positions;
        }
        if (Array.isArray(activity.stops)) {
            strategy.stops = activity.stops;
        }
        if (Array.isArray(activity.legs)) {
            strategy.legs = activity.legs;
        }
        if (activity.session) {
            strategy.session = activity.session;
        }
        if (activity.summary?.netPnl != null) {
            strategy.netPnl = activity.summary.netPnl;
            strategy.summary.netPnl = activity.summary.netPnl;
        }
        if (activity.summary?.status) {
            strategy.status = activity.summary.status;
            strategy.summary.status = activity.summary.status;
        }
        if (activity.capturedAt) {
            strategy.summary.lastHeartbeatAt = activity.capturedAt;
        }
    });

    if (!accountMap.size) {
        const fallbackId = state.summary?.accountId || 'primary';
        accountMap.set(fallbackId, {
            accountId: fallbackId,
            name: state.summary?.accountName || 'Primary Account',
            alias: state.summary?.accountAlias || state.summary?.accountName || fallbackId,
            client: state.summary?.client || state.summary?.accountAlias || state.summary?.accountName || fallbackId,
            strategies: new Map([
                [
                    state.summary?.strategyId || STRATEGY_ID,
                    {
                        strategyId: state.summary?.strategyId || STRATEGY_ID,
                        name: state.summary?.strategyLabel || STRATEGY_ID,
                        status: state.summary?.status || 'initialising',
                        netPnl: state.summary?.netPnl ?? 0,
                        layers: Array.isArray(state.layers) ? state.layers : [],
                        events: Array.isArray(state.events) ? state.events : [],
                        orders: Array.isArray(activityEntries[0]?.orders) ? activityEntries[0].orders : [],
                        positions: Array.isArray(activityEntries[0]?.positions) ? activityEntries[0].positions : [],
                        legs: Array.isArray(activityEntries[0]?.legs) ? activityEntries[0].legs : [],
                        session: activityEntries[0]?.session || null,
                        summary: {
                            ...(state.summary || {}),
                            strategyId: state.summary?.strategyId || STRATEGY_ID,
                            strategyLabel: state.summary?.strategyLabel || STRATEGY_ID,
                            accountId: fallbackId,
                            accountName: state.summary?.accountName || 'Primary Account',
                            accountAlias: state.summary?.accountAlias || fallbackId,
                            client: state.summary?.client || fallbackId,
                        },
                    },
                ],
            ]),
        });
    }

    return Array.from(accountMap.values()).map((account) => {
        const strategies = Array.from(account.strategies.values()).map((strategy) => {
            const layersSorted = [...strategy.layers].sort((a, b) => {
                const aTime = a.capturedAt ? new Date(a.capturedAt).getTime() : 0;
                const bTime = b.capturedAt ? new Date(b.capturedAt).getTime() : 0;
                return bTime - aTime;
            });
            const eventsSorted = [...strategy.events].sort((a, b) => {
                const aTime = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
                const bTime = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
                return bTime - aTime;
            });
            const netPnl = strategy.netPnl ?? layersSorted[0]?.netPnl ?? strategy.summary.netPnl ?? 0;
            const status = strategy.summary.status || strategy.status || 'initialising';

            return {
                ...strategy,
                status,
                netPnl,
                layers: layersSorted,
                events: eventsSorted.slice(0, 50),
                summary: {
                    ...strategy.summary,
                    status,
                    netPnl,
                    lastHeartbeatAt:
                        strategy.summary.lastHeartbeatAt ||
                        layersSorted[0]?.capturedAt ||
                        eventsSorted[0]?.occurredAt ||
                        null,
                    lastMessage: strategy.summary.lastMessage || eventsSorted[0]?.message || null,
                },
            };
        });

        const netPnl = strategies.reduce((total, strategy) => total + (Number(strategy.netPnl) || 0), 0);
        const liveLayers = strategies.reduce(
            (sum, strategy) =>
                sum +
                (strategy.layers?.filter((layer) => (layer.status || '').toLowerCase() === 'active').length || 0),
            0
        );
        const pendingLayers = strategies.reduce(
            (sum, strategy) =>
                sum +
                (strategy.layers?.filter((layer) =>
                    ['armed', 'pending'].includes((layer.status || '').toLowerCase())
                ).length || 0),
            0
        );

        return {
            accountId: account.accountId,
            name: account.name,
            alias: account.alias,
            client: account.client || account.alias || account.name || account.accountId,
            status: deriveAccountStatus(strategies),
            netPnl,
            balance: account.balance ?? null,
            risk: account.risk ?? null,
            metrics: {
                strategies: strategies.length,
                liveLayers,
                pendingLayers,
            },
            strategies,
        };
    });
};

const refreshAccounts = (baseState) => ({
    ...baseState,
    accounts: deriveAccounts(baseState),
});

export const usePelicanMonitor = () => {
    const [state, setState] = useState(() => createInitialState());
    const [connection, setConnection] = useState('pending'); // pending | connected | error
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [nextResetAt, setNextResetAt] = useState(() => getNextTradingDayReset().toISOString());
    const [isStale, setIsStale] = useState(true);
    const streamRef = useRef(null);
    const resetTimerRef = useRef(null);

    useEffect(() => {
        let isMounted = true;

        const fetchInitialState = async () => {
            try {
                const response = await fetch(resolveUrl(`/api/strategies/${STRATEGY_ID}/state`), {
                    headers: {
                        Accept: 'application/json',
                        ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const payload = await response.json();
                if (!isMounted) {
                    return;
                }

                setState(
                    refreshAccounts({
                        strategyId: payload.strategyId || STRATEGY_ID,
                        summary: {
                            ...createInitialSummary(),
                            ...(payload.summary || {}),
                        },
                        layers: Array.isArray(payload.layers) ? payload.layers : [],
                        events: Array.isArray(payload.events) ? payload.events.map(normaliseEvent) : [],
                        activity: Array.isArray(payload.activity) ? payload.activity : [],
                        accounts: Array.isArray(payload.accounts) ? payload.accounts : [],
                    })
                );
                setConnection('connected');
                setLastUpdatedAt(payload?.summary?.lastHeartbeatAt || null);
            } catch (error) {
                if (!isMounted) {
                    return;
                }
                setConnection('error');
            }
        };

        fetchInitialState();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const scheduleReset = () => {
            if (resetTimerRef.current) {
                clearTimeout(resetTimerRef.current);
            }

            const now = new Date();
            const nextResetDate = getNextTradingDayReset(now);
            setNextResetAt(nextResetDate.toISOString());

            const delay = Math.max(1000, nextResetDate.getTime() - now.getTime());
            resetTimerRef.current = setTimeout(() => {
                setState(createInitialState());
                setLastUpdatedAt(null);
                setIsStale(true);
                scheduleReset();
            }, delay);
        };

        scheduleReset();

        return () => {
            if (resetTimerRef.current) {
                clearTimeout(resetTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isSseSupported) {
            return () => {};
        }

        const streamUrl = resolveUrl(`/api/stream?strategy=${STRATEGY_ID}`);
        streamRef.current = new EventSource(streamUrl, {
            withCredentials: !API_BASE,
        });

        streamRef.current.onopen = () => {
            setConnection('connected');
        };

        streamRef.current.onerror = () => {
            setConnection('error');
        };

        streamRef.current.onmessage = (event) => {
            if (!event?.data) {
                return;
            }
            try {
                const parsed = JSON.parse(event.data);
                if (parsed.strategyId && parsed.strategyId !== STRATEGY_ID) {
                    return;
                }

                if (parsed.type === 'state') {
                    const payload = parsed.payload || {};
                    setState(
                        refreshAccounts({
                            strategyId: payload.strategyId || STRATEGY_ID,
                            summary: {
                                ...createInitialSummary(),
                                ...(payload.summary || {}),
                            },
                            layers: Array.isArray(payload.layers) ? payload.layers : [],
                            events: Array.isArray(payload.events) ? payload.events.map(normaliseEvent) : [],
                            activity: Array.isArray(payload.activity) ? payload.activity : [],
                            accounts: Array.isArray(payload.accounts) ? payload.accounts : [],
                        })
                    );
                    setLastUpdatedAt(payload?.summary?.lastHeartbeatAt || null);
                    setConnection('connected');
                    return;
                }

                if (parsed.type === 'layerSnapshot') {
                    setState((current) => refreshAccounts(applyLayerSnapshot(current, parsed.payload)));
                    setLastUpdatedAt(parsed.payload?.capturedAt || new Date().toISOString());
                } else if (parsed.type === 'strategyEvent') {
                    setState((current) => refreshAccounts(appendEvent(current, parsed.payload)));
                    setLastUpdatedAt(parsed.payload?.occurredAt || new Date().toISOString());
                } else if (parsed.type === 'activitySnapshot') {
                    setState((current) => refreshAccounts(applyActivitySnapshot(current, parsed.payload)));
                    setLastUpdatedAt(parsed.payload?.capturedAt || new Date().toISOString());
                }
            } catch (_err) {
                // ignore malformed payloads
            }
        };

        return () => {
            if (streamRef.current) {
                streamRef.current.close();
            }
        };
    }, []);

    useEffect(() => {
        const updated = lastUpdatedAt ? new Date(lastUpdatedAt) : null;
        const boundary = getCurrentTradingDayStart().getTime();
        const stale =
            !updated ||
            Number.isNaN(updated.getTime()) ||
            updated.getTime() < boundary ||
            Date.now() - updated.getTime() > FRESHNESS_THRESHOLD_MS;
        setIsStale((previous) => (previous === stale ? previous : stale));
    }, [lastUpdatedAt]);

    const freshness = useMemo(
        () => ({
            lastUpdatedAt,
            nextResetAt,
            isStale,
        }),
        [lastUpdatedAt, nextResetAt, isStale]
    );

    return {
        state,
        connection,
        lastUpdatedAt,
        nextResetAt,
        isStale,
        freshness,
    };
};
